import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import api, { apiError } from "./api";

export default function Products() {
  const [products, setProducts] = useState(null);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [name, setName] = useState("");

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, []);

  const openDialog = (initial) => {
    setDialog({ open: true, initial });
    setName(initial ? initial.name : "");
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Product name is required");
    try {
      if (dialog.initial) await api.put(`/products/${dialog.initial.id}`, { name: name.trim() });
      else await api.post("/products", { name: name.trim() });
      toast.success("Product saved");
      setDialog({ open: false, initial: null });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete product "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="qc-products-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#8A9A91]">Master Data</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Product Master</h1>
          <p className="mt-1 text-sm text-[#8A9A91]">
            Products the company manufactures. QC picks from this list when entering invoices.
          </p>
        </div>
        <Button data-testid="qc-products-add" onClick={() => openDialog(null)}
          className="rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
          <Plus className="mr-1.5 h-4 w-4" /> Add Product
        </Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-[#E8E6E1] bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product Name</TableHead>
              <TableHead>Added</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(products || []).map((p) => (
              <TableRow key={p.id} data-testid={`qc-product-row-${p.id}`}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="text-[#8A9A91]">{new Date(p.created_at).toLocaleDateString("en-IN")}</TableCell>
                <TableCell className="text-right">
                  <button data-testid={`qc-product-edit-${p.id}`} onClick={() => openDialog(p)}
                    className="rounded-full p-2 text-[#8A9A91] hover:bg-[#1C3F2B]/5 hover:text-[#1C3F2B]">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid={`qc-product-delete-${p.id}`} onClick={() => remove(p)}
                    className="rounded-full p-2 text-[#8A9A91] hover:bg-red-50 hover:text-red-700">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {products && products.length === 0 && (
              <TableRow><TableCell colSpan={3} className="py-10 text-center text-sm text-[#8A9A91]">No products yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="qc-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{dialog.initial ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Product Name</Label>
              <Input data-testid="qc-product-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Hydrolyzed Soy Lecithin" />
            </div>
            <Button data-testid="qc-product-save" onClick={save} className="w-full rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
              Save product
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
